import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import socket from 'shared/lib/socket';
import AlertBarActions from 'flux/actions/AlertBarActions';
import OnlineIndicatorActions from 'flux/actions/OnlineIndicatorActions';
import { Map } from 'immutable';
import { isChrome } from 'shared/lib/browserCheck';

let disconnectBannerDelay;

/**
 * OnlineIndicatorStore
 *
 * Keeps track of online/offline/connectivity state
 *
 */
export default Reflux.createStore({
  mixins: [BaseMixin],

  init() {
    this.setState(
      Map({
        socketOnline: false,
        internetOnline: true,
      }),
    );

    const sock = socket.get();

    sock.on('connect', this._onSocketConnect.bind(null, 'connect'));
    sock.on('reconnect', this._onSocketConnect.bind(null, 'reconnect'));
    sock.on('disconnect', this._onSocketDisconnect);
    window.addEventListener('offline', this._handleWindowDisconnect);
    window.addEventListener('online', this._handleWindowConnect);

    this._socket = sock;

    //Chrome appears to be the only user agent we have trouble with getting immediate response
    //from socket when network is disconnected

    // eslint-disable-next-line no-constant-condition
    if (isChrome() && false) {
      //temporarily disable this as it's not functioning properly TODO fix

      setTimeout(() => this._setupIntervalPongCheck(), 5000);
    }
  },

  _setupIntervalPongCheck() {
    /**
     * This was added for this issue: https://github.com/SHOFLO/Issues/issues/2486
     * If we don't get a 'pong' then it's a bad sign!
     * @type {number}
     */
    let lastPong = new Date().getTime();
    // eslint-disable-next-line no-unused-vars
    this._socket.on('pong', (latencyInMs) => {
      lastPong = new Date().getTime();
    });

    const checkOfflineInterval = 5000;

    setInterval(() => {
      if (new Date().getTime() - lastPong > checkOfflineInterval) {
        this._onDisconnect();
      } else {
        this._onConnect();
      }
    }, checkOfflineInterval);
  },

  _onSocketConnect() {
    if (this.getInState('internetOnline')) {
      this._clearDisconnectBannerDelay();
      AlertBarActions.hide();
      // this is used to renable key bindings after reconnect
      OnlineIndicatorActions.setConnected();
    }

    if (!this.getInState('socketOnline')) {
      const next = this.getState().set('socketOnline', true);
      this.setStateAndTrigger(next);
    }
  },

  _onSocketDisconnect() {
    // this is used to disable key bindings to prevent edits
    OnlineIndicatorActions.setDisconnected();

    const next = this.getState().set('socketOnline', false);

    this.setStateAndTrigger(next);
    if (!disconnectBannerDelay) {
      disconnectBannerDelay = setTimeout(this._showDisconnectAlertBar, 6000);
    }
  },

  _onInternetDisconnect() {
    OnlineIndicatorActions.setDisconnected();
    const next = this.getState().set('internetOnline', false);

    this.setStateAndTrigger(next);
    if (!disconnectBannerDelay) {
      disconnectBannerDelay = setTimeout(this._showDisconnectAlertBar, 3000);
    }
  },

  _onInternetConnect() {
    if (this.getInState('socketOnline')) {
      this._clearDisconnectBannerDelay();
      AlertBarActions.hide();
      OnlineIndicatorActions.setConnected();
    }
    const next = this.getState().set('internetOnline', true);

    this.setStateAndTrigger(next);
  },

  //if the disconnect banner hasn't displayed yet, clear it
  _clearDisconnectBannerDelay() {
    if (disconnectBannerDelay) {
      clearTimeout(disconnectBannerDelay);
    }
  },

  _showDisconnectAlertBar() {
    AlertBarActions.show({
      message: 'Connection lost. Trying to reconnect',
      type: 'warning',
      dismissible: false,
      size: 'small',
      dots: true,
      title: 'disconnect',
    });
  },

  _handleWindowDisconnect() {
    this._onInternetDisconnect();
  },

  _handleWindowConnect() {
    this._onInternetConnect();
  },
});
